import React from "react";
import { CheckCircle, AlertCircle, XCircle, Clock } from "lucide-react";
import type { ProcessedMonitor } from "../types";
import { HeartbeatBar } from "./HeartbeatBar";
import { formatRelativeTimeHu } from "../utils";

interface ServiceCardProps {
  monitor: ProcessedMonitor;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({ monitor }) => {
  const latestCheckSuccessful = monitor.heartbeats.at(-1)?.status === 1;
  const getStatusBadge = () => {
    switch (monitor.status) {
      case "operational":
        if (!latestCheckSuccessful) {
          return {
            label: "Nincs friss ellenőrzés",
            icon: Clock,
            className: "bg-[#f3f1ec] text-[#62666b] border-[#dedbd4]",
            iconColor: "text-[#62666b]",
          };
        }
        return {
          label: "Üzemel",
          icon: CheckCircle,
          className: "bg-[#e7f1eb] text-[#246047] border-[#b8dec9]",
          iconColor: "text-[#246047]",
        };
      case "degraded":
        return {
          label: latestCheckSuccessful ? "Legutóbbi ellenőrzés sikeres" : "Részleges probléma",
          icon: AlertCircle,
          className: "bg-[#faf0d9] text-[#775219] border-[#edd6a4]",
          iconColor: "text-[#775219]",
        };
      case "down":
        return {
          label: "Leállás",
          icon: XCircle,
          className: "bg-[#fae9ea] text-[#922d35] border-[#f0bec2]",
          iconColor: "text-[#922d35]",
        };
      case "maintenance":
        return {
          label: "Karbantartás",
          icon: Clock,
          className: "bg-[#eaf0f5] text-[#365570] border-[#c6d8e6]",
          iconColor: "text-[#365570]",
        };
      default:
        return {
          label: "Ismeretlen",
          icon: Clock,
          className: "bg-[#f3f1ec] text-[#62666b] border-[#dedbd4]",
          iconColor: "text-[#62666b]",
        };
    }
  };

  const badge = getStatusBadge();
  const Icon = badge.icon;

  return (
    <div className="bg-white border border-[#dedbd4] rounded-[0.375rem] p-4 sm:p-5">
      {/* Top row: Name, ping, status badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 mb-3.5">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-[#1b2a47] text-base">
              {monitor.name}
            </h4>
          </div>
          {monitor.lastCheckTime && (
            <p className="text-xs text-[#8c8982] mt-0.5">
              Ellenőrizve: {formatRelativeTimeHu(monitor.lastCheckTime)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {monitor.currentPingMs !== null && (
            <div
              className="text-xs text-[#62666b] font-mono"
              title="Legutóbbi válaszidő"
            >
              Válaszidő:{" "}
              <span className="text-[#1b2a47] font-semibold">
                {monitor.currentPingMs} ms
              </span>
            </div>
          )}

          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-[0.375rem] text-xs font-semibold border ${badge.className}`}
          >
            <Icon className={`w-3.5 h-3.5 ${badge.iconColor}`} />
            <span>{badge.label}</span>
          </div>
        </div>
      </div>

      {/* Heartbeat visualization */}
      <HeartbeatBar
        heartbeats={monitor.heartbeats}
        uptimePercent={monitor.uptimePercent}
      />
    </div>
  );
};
